import { MapPin, Calendar, Smartphone, Send, ExternalLink, ChevronRight } from "lucide-react";
import { type FileMetadata } from "@/lib/api";
import {
  getVerdictStyle,
  getContentTypeMeta,
  getCaptured,
  getDateConfidenceLabel,
  getLocationText,
  getDeviceText,
  getSourceText,
  mapsUrl,
  formatCapture,
  hasFileIntel,
} from "@/lib/fileIntelligence";

/** Compact horizontal File Intelligence strip for the report header.
 *  Mirrors the modal's data but laid out as a single inline row; auto-hides
 *  when the job carries no usable metadata. When `onClick` is provided the whole
 *  strip becomes a button (opens the project-info modal) with a chevron affordance. */
export function FileMetadataStrip({
  fm,
  onClick,
}: {
  fm?: FileMetadata | null;
  onClick?: () => void;
}) {
  if (!hasFileIntel(fm)) return null;

  const vs = getVerdictStyle(fm.verdict);
  const { label: ctypeLabel, Icon: ContentIcon } = getContentTypeMeta(fm.audio?.content_type);
  const captured = getCaptured(fm);
  const conf = getDateConfidenceLabel(fm);
  const location = getLocationText(fm);
  const url = mapsUrl(fm);
  const device = getDeviceText(fm);
  const source = getSourceText(fm);

  const interactive = Boolean(onClick);
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (onClick && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div className="px-6 pt-3 shrink-0">
      <div
        role={interactive ? "button" : undefined}
        tabIndex={interactive ? 0 : undefined}
        onClick={onClick}
        onKeyDown={interactive ? handleKeyDown : undefined}
        aria-label={interactive ? "View file details" : undefined}
        className={`flex items-center gap-2 border rounded-lg bg-muted/30 px-3 py-2 transition-colors ${
          interactive
            ? "cursor-pointer hover:bg-muted/60 hover:border-foreground/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            : ""
        }`}
      >
        <div className="flex flex-1 min-w-0 flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-muted-foreground">
          {/* Content type */}
          <span className="inline-flex items-center gap-1.5 text-foreground font-medium">
            <ContentIcon className="w-3.5 h-3.5 text-primary shrink-0" />
            {ctypeLabel}
          </span>

          {/* Verdict badge */}
          {fm.verdict?.[0] && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-medium"
              style={{ backgroundColor: vs.bg, color: vs.color }}
              title={fm.verdict_tip || fm.verdict_human || undefined}
            >
              <vs.Icon className="w-3 h-3 shrink-0" />
              {vs.label}
            </span>
          )}

          {/* Captured */}
          {captured && (
            <span className="inline-flex items-center gap-1">
              <Calendar className="w-3 h-3 shrink-0" />
              {formatCapture(captured)}
              {conf ? ` (${conf})` : ""}
            </span>
          )}

          {/* Location */}
          {location && (
            <span className="inline-flex items-center gap-1">
              <MapPin className="w-3 h-3 shrink-0" />
              {url ? (
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="text-blue-600 hover:underline inline-flex items-center gap-0.5"
                >
                  {location}
                  <ExternalLink className="w-2.5 h-2.5 opacity-70 shrink-0" />
                </a>
              ) : (
                location
              )}
            </span>
          )}

          {/* Device */}
          {device && (
            <span className="inline-flex items-center gap-1 truncate max-w-[40%]" title={device}>
              <Smartphone className="w-3 h-3 shrink-0" />
              <span className="truncate">{device}</span>
            </span>
          )}

          {/* Source */}
          {source && (
            <span className="inline-flex items-center gap-1">
              <Send className="w-3 h-3 shrink-0" />
              {source}
            </span>
          )}
        </div>

        {interactive && (
          <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" aria-hidden="true" />
        )}
      </div>
    </div>
  );
}
